// ─────────────────────────────────────────────
// KLOTHIQ — Customer List Page (wired)
// ─────────────────────────────────────────────

import { Input } from '@/components/ui/FormControls';
import { useState } from 'react';
import Link from '@/components/common/AccessLink';
import { useCustomers } from '@/hooks/useCustomers';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import ExportActions from '@/components/common/ExportActions';
import { Plus, Users, Search, Pencil, Phone, ChevronRight } from 'lucide-react';

export default function CustomerListPage() {
  const [search, setSearch] = useState('');
  const { data: customers, isLoading, error, refetch } = useCustomers(search);

  const filterSummaryText = search ? `Search: "${search}"` : 'All Customers';

  return (
    <div>
      <PageHeader
        title="Customers"
        description="Manage your customer records"
        actions={
          <div className="flex items-center gap-2">
            <ExportActions
              excelOptions={() => ({
                fileName: `Customers_List_${new Date().toISOString().split('T')[0]}`,
                sheetName: 'Customers',
                filtersSummary: filterSummaryText,
                columns: [
                  { header: 'Name',    key: 'name' },
                  { header: 'Phone',   key: 'phone' },
                  { header: 'Email',   key: 'email' },
                  { header: 'Address', key: 'address' },
                  { header: 'City',    key: 'city' },
                  { header: 'Pincode', key: 'pincode' },
                ],
                data: (customers ?? []).map((c) => ({
                  name:    c.name,
                  phone:   c.phone ?? '—',
                  email:   (c as any).email ?? '—',
                  address: (c as any).address ?? '—',
                  city:    c.city ?? '—',
                  pincode: c.pincode ?? '—',
                })),
              })}
              pdfOptions={() => ({
                fileName: `Customers_List_${new Date().toISOString().split('T')[0]}`,
                title: 'Customer Directory Report',
                subtitle: `Exported on ${new Date().toLocaleDateString('en-IN')}`,
                filtersSummary: filterSummaryText,
                columns: [
                  { header: 'Name',    key: 'name' },
                  { header: 'Phone',   key: 'phone' },
                  { header: 'City',    key: 'city' },
                  { header: 'Pincode', key: 'pincode' },
                ],
                rows: (customers ?? []).map((c) => ({
                  name:    c.name,
                  phone:   c.phone ?? '—',
                  city:    c.city ?? '—',
                  pincode: c.pincode ?? '—',
                })),
                summaryLines: [
                  { label: 'Total Customers', value: (customers ?? []).length.toString() },
                ],
              })}
            />
            <Link to="/customers/new" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90">
              <Plus className="h-4 w-4" /> Add Customer
            </Link>
          </div>
        }
      />

      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="customer-search"
            type="text"
            placeholder="Search customers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            helpLabel="Search records"
          />
        </div>
      </div>

      {isLoading && <LoadingState message="Loading customers…" />}
      {error && <ErrorState message={error.message} onRetry={refetch} />}

      {!isLoading && !error && customers?.length === 0 && (
        <EmptyState
          icon={<Users className="h-12 w-12" />}
          title={search ? 'No customers match your search' : 'No customers yet'}
          description={search ? 'Try a different search term' : 'Add your first customer to start recording sales'}
          action={!search ? (
            <Link to="/customers/new" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
              <Plus className="h-4 w-4" /> Add Customer
            </Link>
          ) : undefined}
        />
      )}

      {!isLoading && !error && customers && customers.length > 0 && (
        <>
          {/* Desktop */}
          <div className="obsidian-surface hidden overflow-hidden rounded-lg border border-border bg-card shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Phone</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">City</th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">Pincode</th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {customers.map((c) => (
                  <tr key={c.id} className="transition-colors hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium text-foreground">{c.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {c.phone ? (
                        <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-primary">
                          <Phone className="h-3 w-3" />{c.phone}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{c.city ?? '—'}</td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">{c.pincode ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <Link to={`/customers/${c.id}/edit`} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="obsidian-surface divide-y divide-border overflow-hidden rounded-lg border border-border bg-card shadow-sm md:hidden">
            {customers.map((c) => (
              <Link key={c.id} to={`/customers/${c.id}/edit`} className="flex items-center justify-between px-4 py-3 hover:bg-muted/30">
                <div>
                  <p className="font-medium text-foreground">{c.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {c.phone ?? 'No phone'}{c.city ? ` · ${c.city}` : ''}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            {customers.length} customer{customers.length !== 1 ? 's' : ''}
          </p>
        </>
      )}
    </div>
  );
}
